import { FC, useEffect, useRef, useState } from "react";
import { ChatMessage } from "./useChatWidget";

interface ChatPopupProps {
  messages: ChatMessage[];
  sending: boolean;
  awaitingConfirmation: boolean;
  autoListenSignal: number;
  onSend: (text: string, viaVoice?: boolean) => void;
}

const MIC_ERROR_MESSAGES: Record<string, string> = {
  "not-allowed": "Microphone access was blocked. Check the site permissions and allow microphone access.",
  "service-not-allowed": "Speech recognition service was blocked by the browser.",
  "no-speech": "Didn't catch that — no speech detected. Try again.",
  "audio-capture": "No microphone was found. Check your device.",
  network: "Speech recognition needs network access — check your connection.",
};

const ChatPopup: FC<ChatPopupProps> = ({ messages, sending, awaitingConfirmation, autoListenSignal, onSend }) => {
  const [text, setText] = useState("");
  const [micStatus, setMicStatus] = useState("");
  const [listening, setListening] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const listeningRef = useRef(false);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, sending]);

  const handleSend = () => {
    if (!text.trim()) return;
    setVoiceMode(false);
    onSend(text);
    setText("");
  };

  const SpeechRecognitionCtor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  const micSupported = Boolean(SpeechRecognitionCtor) && window.isSecureContext;

  const startListening = () => {
    if (listeningRef.current) return; // already listening, avoid double-start
    if (!SpeechRecognitionCtor) {
      setMicStatus("Voice input isn't supported in this browser — try Chrome or Edge.");
      return;
    }
    if (!window.isSecureContext) {
      setMicStatus("Voice input needs https:// (secure context).");
      return;
    }
    // If the assistant is still talking when we start listening again (or the
    // user re-clicks mid-reply), stop it immediately rather than talking over
    // the next thing the user says.
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        stream.getTracks().forEach((t) => t.stop());
        beginRecognition();
      })
      .catch((e: any) => setMicStatus(`Microphone permission denied: ${e.message}`));
  };

  const beginRecognition = () => {
    const recognition = new SpeechRecognitionCtor();
    recognitionRef.current = recognition;
    recognition.lang = "en-US";
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    let finalTranscript = "";

    recognition.addEventListener("start", () => {
      finalTranscript = "";
      listeningRef.current = true;
      setListening(true);
      setVoiceMode(true);
      setMicStatus("Listening...");
    });
    recognition.addEventListener("result", (event: any) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalTranscript += transcript;
        else interim += transcript;
      }
      setText((finalTranscript + interim).trim());
    });
    recognition.addEventListener("error", (event: any) => {
      listeningRef.current = false;
      setListening(false);
      setMicStatus(MIC_ERROR_MESSAGES[event.error] || `Mic error: ${event.error}`);
      if (event.error !== "no-speech") setVoiceMode(false);
    });
    recognition.addEventListener("end", () => {
      listeningRef.current = false;
      setListening(false);
      setMicStatus("");
      const finalText = finalTranscript.trim();
      if (finalText) {
        onSend(finalText, true);
        setText("");
      } else {
        // Nothing was said — don't silently sit in voice mode forever.
        setVoiceMode(false);
      }
    });

    try {
      recognition.start();
    } catch (e: any) {
      setMicStatus(`Could not start listening: ${e.message}`);
    }
  };

  const toggleMic = () => {
    if (listening) {
      setVoiceMode(false);
      recognitionRef.current?.stop();
      return;
    }
    if (voiceMode) {
      // Between turns (assistant speaking, about to auto-listen again) —
      // clicking mic here means "stop the hands-free loop", not "listen now".
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
      setVoiceMode(false);
      setMicStatus("");
      return;
    }
    startListening();
  };

  // After the assistant finishes speaking a reply to a voice-initiated message,
  // useChatWidget bumps autoListenSignal — resume listening automatically for
  // a hands-free back-and-forth, without requiring another mic click.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (voiceMode) startListening();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoListenSignal]);

  return (
    <div className="card shadow chat-widget-popup">
      <div className="card-header d-flex align-items-center justify-content-between py-3 bg-primary">
        <span className="fw-bold text-white">Portal Assistant</span>
        <span className="badge bg-white text-primary">Beta</span>
      </div>

      <div className="card-body overflow-auto d-flex flex-column gap-3 chat-widget-log" ref={logRef}>
        {messages.map((m) => (
          <div key={m.id} className={`d-flex ${m.who === "user" ? "justify-content-end" : "justify-content-start"}`}>
            <div
              className={
                "chat-widget-bubble-msg px-3 py-2 rounded-3 " +
                (m.who === "user"
                  ? "bg-primary text-white"
                  : m.error
                  ? "bg-light-danger text-danger"
                  : m.pending
                  ? "bg-light-warning text-gray-800 border border-warning"
                  : "bg-light-primary text-gray-800")
              }
            >
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="d-flex justify-content-start">
            <div className="chat-widget-bubble-msg px-3 py-2 rounded-3 bg-light-primary chat-widget-typing">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}
      </div>

      {(awaitingConfirmation || micStatus || voiceMode) && (
        <div className="px-3 py-1 fs-8 text-muted border-top">
          {awaitingConfirmation
            ? "Waiting for your confirmation — reply yes or no."
            : micStatus || "Voice mode on — click the mic to stop."}
        </div>
      )}

      <div className="card-footer p-2">
        <div className="input-group">
          <button
            type="button"
            className={`btn btn-icon ${listening ? "btn-danger text-white" : voiceMode ? "btn-warning" : "btn-light"}`}
            onClick={toggleMic}
            disabled={!micSupported && Boolean(SpeechRecognitionCtor) === false}
            title={listening ? "Stop listening" : voiceMode ? "Voice mode on — click to stop" : "Speak"}
          >
            <i className={`bi ${listening ? "bi-mic-fill" : "bi-mic"} fs-4`} />
          </button>
          <input
            type="text"
            className="form-control"
            placeholder="Ask me anything about portalv2..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSend();
            }}
          />
          <button type="button" className="btn btn-primary" onClick={handleSend} disabled={sending}>
            <i className="bi bi-send fs-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export { ChatPopup };
